import * as React from 'react';

import { Button } from './Button';
import { Surface } from './Surface';
import { Heading, Text } from './Typography';
import { cn } from './cn';

// Card family — closed-shape compositions over the Surface paint
// primitive. Cards group content into a visually distinct container
// with title / description / value / actions slots and a uniform
// header-body-actions-footer layout.
//
// Visual contract: tone in `neutral (default) | muted | primary
// | secondary`. Status / feedback semantics (info, success, warning,
// destructive) belong to Alert / Toast / a Surface composed by a
// claim-bearing primitive — a Card is a container, not a claim, so
// we deliberately surface only the brand half of Surface's tone
// palette here.
//
// `*ClassName` slot escape hatches don't exist: typography lives in
// `<Text>` / `<Heading>`, layout lives in the consumer's wrapper. If a
// consumer thinks they need a className override, the right fix is to
// compose differently.

// Each tone: optional inverted-on-hover text classes for the loud
// (primary / secondary) surfaces, applied via `tw-group` on Surface.
// Quiet surfaces (neutral / muted) have no inversion to make.
const CARD_TEXT_HOVER_CLASSNAMES = {
    neutral: null,
    muted: null,
    primary: 'group-hover:tw-text-foreground',
    secondary: 'group-hover:tw-text-secondary-foreground'
};

const IMAGE_CARD_DIVIDER_CLASSNAMES = {
    neutral: 'tw-border-t tw-border-solid tw-border-input',
    muted: 'tw-border-t tw-border-solid tw-border-border',
    primary: 'tw-border-t tw-border-solid tw-border-primary',
    secondary: 'tw-border-t tw-border-solid tw-border-secondary'
};

const CARD_GROUP_COLUMN_CLASSNAMES = {
    auto: 'tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4',
    2: 'tw-grid-cols-1 md:tw-grid-cols-2',
    3: 'tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3',
    4: 'tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4',
    5: 'tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-5'
};

const CARD_GROUP_GAP_CLASSNAMES = {
    small: 'tw-gap-2',
    medium: 'tw-gap-4',
    large: 'tw-gap-6'
};

const CARD_ACTIONS_ROW_CLASSNAME = 'tw-mt-auto tw-flex tw-flex-wrap tw-gap-2 tw-pt-4';

// Each (tone, role) cell resolves to a (tone, appearance) tuple passed
// straight to <Button>. Choices follow the same logic as before: on
// quiet surfaces (neutral / muted) promote both actions with their own
// tone solid; on loud surfaces (primary / secondary) drop the primary
// action to neutral outline so the surface stays the loudest element.
const CARD_ACTION_STYLE_KEYS = {
    neutral: {
        primary: { tone: 'primary', appearance: 'solid' },
        secondary: { tone: 'secondary', appearance: 'solid' }
    },
    muted: {
        primary: { tone: 'primary', appearance: 'solid' },
        secondary: { tone: 'secondary', appearance: 'solid' }
    },
    primary: {
        primary: { tone: 'neutral', appearance: 'outline' },
        secondary: { tone: 'secondary', appearance: 'solid' }
    },
    secondary: {
        primary: { tone: 'neutral', appearance: 'outline' },
        secondary: { tone: 'primary', appearance: 'solid' }
    }
};

// Slot → which Typography primitive + variant. Heading slots (title,
// value) render as <Heading>; description and body render as <Text>.
// Each slot uses tone="inherit" — the wrapping div carries the colour
// resolved from the card's tone.
const CARD_SLOT_TYPOGRAPHY = {
    title: { component: 'heading', variant: 'title-sm' },
    description: { component: 'text', variant: 'caption' },
    body: { component: 'text', variant: 'body' },
    value: { component: 'heading', variant: 'display' }
};

const CARD_TONES = ['neutral', 'muted', 'primary', 'secondary'];

const resolveCardTone = (tone) => (
    CARD_TONES.includes(tone) ? tone : 'neutral'
);

const resolveCardSlotHoverClassName = ({ interactive, tone }) => (
    interactive ? CARD_TEXT_HOVER_CLASSNAMES[resolveCardTone(tone)] : null
);

const CardSlot = ({ children, interactive, slot, tone, ...rest }) => {
    const { component, variant: typographyVariant } = CARD_SLOT_TYPOGRAPHY[slot];
    const Typography = component === 'heading' ? Heading : Text;

    return (
        <div className={cn(resolveCardSlotHoverClassName({ interactive, tone }))} {...rest}>
            <Typography as="span" tone="inherit" variant={typographyVariant}>
                {children}
            </Typography>
        </div>
    );
};

const resolveImageDividerClassName = ({ tone }) => {
    const resolvedTone = resolveCardTone(tone);

    return IMAGE_CARD_DIVIDER_CLASSNAMES[resolvedTone];
};

const resolveCardActionStyle = ({ role, tone }) => {
    const resolvedTone = resolveCardTone(tone);

    return CARD_ACTION_STYLE_KEYS[resolvedTone]?.[role] ?? null;
};

const normalizeCardAction = action => {
    if (action === undefined || action === null) {
        return null;
    }

    if (typeof action === 'string' || React.isValidElement(action)) {
        return { label: action };
    }

    return action;
};

const CardActionsBlock = ({
    actionsFullWidth = false,
    primaryAction,
    secondaryAction,
    tone = 'neutral'
}) => {
    const resolvedPrimaryAction = normalizeCardAction(primaryAction);
    const resolvedSecondaryAction = normalizeCardAction(secondaryAction);

    if (resolvedPrimaryAction === null && resolvedSecondaryAction === null) {
        return null;
    }

    const renderAction = (action, role) => {
        if (action === null) {
            return null;
        }

        const {
            ariaLabel,
            disabled = false,
            href,
            icon,
            label,
            loading = false,
            onClick,
            type = 'button'
        } = action;

        const buttonStyle = resolveCardActionStyle({ role, tone });

        const handleActionClick = event => {
            event.stopPropagation();
            onClick?.(event);
        };

        const content = (
            <span className="tw-flex tw-items-center tw-gap-2">
                {icon !== undefined ? icon : null}
                <span>{label}</span>
            </span>
        );

        if (href !== undefined) {
            return (
                <Button
                    appearance={buttonStyle?.appearance}
                    asChild
                    fullWidth={actionsFullWidth}
                    key={role}
                    size="small"
                    tone={buttonStyle?.tone}
                >
                    <a
                        aria-label={ariaLabel}
                        aria-disabled={disabled ? true : undefined}
                        className={cn(disabled ? 'tw-pointer-events-none tw-opacity-50' : null)}
                        onClick={event => {
                            if (disabled) {
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                            }

                            handleActionClick(event);
                        }}
                        href={href}
                    >
                        {content}
                    </a>
                </Button>
            );
        }

        return (
            <Button
                appearance={buttonStyle?.appearance}
                aria-label={ariaLabel}
                disabled={disabled}
                fullWidth={actionsFullWidth}
                key={role}
                loading={loading}
                onClick={handleActionClick}
                size="small"
                tone={buttonStyle?.tone}
                type={type}
            >
                {content}
            </Button>
        );
    };

    return (
        <div className={CARD_ACTIONS_ROW_CLASSNAME}>
            {renderAction(resolvedPrimaryAction, 'primary')}
            {renderAction(resolvedSecondaryAction, 'secondary')}
        </div>
    );
};

const CardHeaderBlock = ({ actions, description, interactive = false, title, tone }) => {
    if (title === undefined && description === undefined && actions === undefined) {
        return null;
    }

    if (title === undefined && description === undefined) {
        return <div>{actions}</div>;
    }

    return (
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
            <div className="tw-min-w-0 tw-space-y-1">
                {title !== undefined ? (
                    <CardSlot interactive={interactive} slot="title" tone={tone}>
                        {title}
                    </CardSlot>
                ) : null}
                {description !== undefined ? (
                    <CardSlot interactive={interactive} slot="description" tone={tone}>
                        {description}
                    </CardSlot>
                ) : null}
            </div>
            {actions !== undefined ? (
                <div className="tw-flex tw-shrink-0 tw-items-center">
                    {actions}
                </div>
            ) : null}
        </div>
    );
};

const ContentCard = React.forwardRef(({
    actions,
    actionsFullWidth = false,
    children,
    className: _className,
    style: _style,
    description,
    disabled = false,
    footer,
    href,
    interactive = false,
    onClick,
    onKeyDown,
    primaryAction,
    secondaryAction,
    title,
    tone = 'neutral',
    ...props
}, ref) => {
    const resolvedTone = resolveCardTone(tone);
    const hasCardActions = primaryAction !== undefined || secondaryAction !== undefined;
    const hasHeaderRow = title !== undefined || description !== undefined || actions !== undefined;

    return (
        <Surface
            ref={ref}
            disabled={disabled}
            fullHeight
            href={href}
            interactive={interactive}
            onClick={onClick}
            onKeyDown={onKeyDown}
            rounded="large"
            tone={resolvedTone}
            {...props}
        >
            <div className="tw-flex tw-h-full tw-flex-col">
                <CardHeaderBlock actions={actions} description={description} interactive={interactive} title={title} tone={resolvedTone} />
                {children !== undefined ? (
                    <div className={cn('tw-flex-1 tw-min-h-0', resolveCardSlotHoverClassName({ interactive, tone: resolvedTone }), hasHeaderRow ? 'tw-pt-4' : null)}>
                        <Text tone="inherit" variant="body">{children}</Text>
                    </div>
                ) : null}
                <CardActionsBlock
                    actionsFullWidth={actionsFullWidth}
                    primaryAction={primaryAction}
                    secondaryAction={secondaryAction}
                    tone={resolvedTone}
                />
                {footer !== undefined ? (
                    <div className={cn(hasCardActions ? 'tw-pt-4' : 'tw-mt-auto tw-pt-4')}>
                        {footer}
                    </div>
                ) : null}
            </div>
        </Surface>
    );
});
ContentCard.displayName = 'ContentCard';

const ImageCard = React.forwardRef(({
    actions,
    actionsFullWidth = false,
    children,
    className: _className,
    style: _style,
    description,
    disabled = false,
    footer,
    href,
    image,
    interactive = false,
    onClick,
    onKeyDown,
    primaryAction,
    secondaryAction,
    title,
    tone = 'neutral',
    ...props
}, ref) => {
    const resolvedTone = resolveCardTone(tone);
    const hasCardActions = primaryAction !== undefined || secondaryAction !== undefined;
    const hasHeaderRow = title !== undefined || description !== undefined || actions !== undefined;

    return (
        <Surface
            ref={ref}
            disabled={disabled}
            fullHeight
            href={href}
            interactive={interactive}
            onClick={onClick}
            onKeyDown={onKeyDown}
            padded={false}
            rounded="large"
            tone={resolvedTone}
            {...props}
        >
            <div className="tw-flex tw-h-full tw-flex-col">
                <div className="tw-w-full tw-shrink-0 tw-overflow-hidden [&_img]:tw-block [&_img]:tw-w-full">
                    {image}
                </div>
                <div className={cn('tw-flex tw-flex-1 tw-flex-col tw-p-4', resolveImageDividerClassName({ tone: resolvedTone }))}>
                    <CardHeaderBlock actions={actions} description={description} interactive={interactive} title={title} tone={resolvedTone} />
                    {children !== undefined ? (
                        <div className={cn('tw-flex-1 tw-min-h-0', resolveCardSlotHoverClassName({ interactive, tone: resolvedTone }), hasHeaderRow ? 'tw-pt-4' : null)}>
                            <Text tone="inherit" variant="body">{children}</Text>
                        </div>
                    ) : null}
                    <CardActionsBlock
                        actionsFullWidth={actionsFullWidth}
                        primaryAction={primaryAction}
                        secondaryAction={secondaryAction}
                        tone={resolvedTone}
                    />
                    {footer !== undefined ? (
                        <div className={cn(hasCardActions ? 'tw-pt-4' : 'tw-mt-auto tw-pt-4')}>
                            {footer}
                        </div>
                    ) : null}
                </div>
            </div>
        </Surface>
    );
});
ImageCard.displayName = 'ImageCard';

const StatCard = React.forwardRef(({
    actions,
    actionsFullWidth = false,
    className: _className,
    style: _style,
    disabled = false,
    footer,
    href,
    interactive = false,
    onClick,
    onKeyDown,
    primaryAction,
    secondaryAction,
    title,
    tone = 'neutral',
    value,
    ...props
}, ref) => {
    const resolvedTone = resolveCardTone(tone);
    const hasCardActions = primaryAction !== undefined || secondaryAction !== undefined;

    return (
        <Surface
            ref={ref}
            disabled={disabled}
            fullHeight
            href={href}
            interactive={interactive}
            onClick={onClick}
            onKeyDown={onKeyDown}
            rounded="large"
            tone={resolvedTone}
            {...props}
        >
            <div className="tw-flex tw-h-full tw-flex-col">
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                    <CardSlot interactive={interactive} slot="title" tone={resolvedTone}>
                        {title}
                    </CardSlot>
                    {actions !== undefined ? (
                        <div className="tw-flex tw-shrink-0 tw-items-center">
                            {actions}
                        </div>
                    ) : null}
                </div>
                <div className={cn('tw-pt-4', resolveCardSlotHoverClassName({ interactive, tone: resolvedTone }))}>
                    <Heading as="span" tone="inherit" variant="display">{value}</Heading>
                </div>
                <CardActionsBlock
                    actionsFullWidth={actionsFullWidth}
                    primaryAction={primaryAction}
                    secondaryAction={secondaryAction}
                    tone={resolvedTone}
                />
                {footer !== undefined ? (
                    <div className={cn(hasCardActions ? 'tw-flex tw-justify-end tw-pt-4' : 'tw-mt-auto tw-flex tw-justify-end tw-pt-4')}>
                        {footer}
                    </div>
                ) : null}
            </div>
        </Surface>
    );
});
StatCard.displayName = 'StatCard';

const CardGroup = React.forwardRef(({
    children,
    className: _className,
    style: _style,
    columns = 'auto',
    gap = 'medium',
    ...props
}, ref) => {
    const resolvedColumns = CARD_GROUP_COLUMN_CLASSNAMES[columns] ? columns : 'auto';
    const resolvedGap = CARD_GROUP_GAP_CLASSNAMES[gap] ? gap : 'medium';

    return (
        <div
            ref={ref}
            className={cn(
                'tw-grid tw-items-stretch [&>*]:tw-h-full',
                CARD_GROUP_COLUMN_CLASSNAMES[resolvedColumns],
                CARD_GROUP_GAP_CLASSNAMES[resolvedGap]
            )}
            {...props}
        >
            {children}
        </div>
    );
});
CardGroup.displayName = 'CardGroup';

export {
    CardGroup,
    ContentCard,
    ImageCard,
    StatCard
};
